import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from '../../components/Form';
import Row from '../../components/Row';
import Col from '../../components/Col';
import SignatureCanvas from '../../components/SignatureCanvas';
import Button from '../../components/Button';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectValidation } from 'src/store/selectors/Validation';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { clearValidation } from 'src/store/actions/validation';
import Loader from '../../components/Loader';
import { selectLoader } from 'src/store/selectors/loader';
import AddSigneePopup from './AddSigneePopup';
import Textarea from '../../components/Textarea';
import { toast } from 'react-toastify';
import { variables } from 'src/Tenants';
import SuiteExtraInfo from './SuiteExtraInfo';
import KeyPickupServiceRequestList from 'src/pages/Suite/KeyPickupRequest/KeyPickupServiceRequestList';
import KeyPickupDocuments from './KeyPickupDocuments';
import KeyPickupWarrantyDetails from 'src/pages/Suite/KeyPickupWarranty/KeyPickupWarrantyDetails';
import moment from 'moment';
import 'react-toastify/dist/ReactToastify.css';
import './keyhandoff.scss';
import { ROLES } from 'src/utils/MessagingUtils/shared-constants';
import { KeyHandoffParams } from 'src/SharedCustomHooks/UseSuiteActions/useKeyHandoffSubmission';
import {
  SuiteUser,
  useKeyPickupData,
  UseKeyPickupDataReturn,
} from 'src/SharedCustomHooks/UseSuiteActions/useKeyPickupData';
import { Suite } from 'src/SharedCustomHooks/UseSuiteActions/types';

interface KeyhandoffProps {
  onClose: any;
  submitKeyHandoff: (params: KeyHandoffParams) => Promise<boolean>;
  closeKeyPickupDialogueAndRefreshContent?: () => void;
  suiteFormData?: Suite;
}

const KeyHandoffComponent = ({
  onClose,
  submitKeyHandoff,
  closeKeyPickupDialogueAndRefreshContent,
  suiteFormData,
}: KeyhandoffProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthenticatedUser);
  const validation = useSelector(selectValidation);
  const loader = useSelector(selectFormLoader);
  const prLoader = useSelector(selectLoader);

  const {
    serviceRequests,
    importantDocuments,
    suiteUsers,
    isLoading: keyPickupLoading,
    isFetchingSuiteUsers,
    error: keyPickupError,
    fetchSuiteUsersData,
  }: UseKeyPickupDataReturn = useKeyPickupData(suiteFormData?.id);

  const [state, setState] = useState<any>({
    suiteSignatureList: [],
    showAddForm: false,
    homeOwnersSigns: [],
    notes: '',
    isAddFormOpen: false,
    warrantyStartDate: '',
  });

  const handleSignature = (image: any, signType: any, userType: any) => {
    if (image === null) {
      return toast.error('🚫 Please enter a valid signature.', {
        autoClose: 10000,
        position: 'top-center',
      });
    }

    const { suiteSignatureList } = state;
    const currentSignatures: any = suiteSignatureList || [];

    const createSignatureObject = (role: string, userId: string | null) => ({
      userId,
      role,
      signature: image,
      signType,
    });

    if (userType === 'Property Developer') {
      const propertyManagerId = suiteFormData.propertyManager?.id || null;
      const newSignature = createSignatureObject(
        'Property Developer',
        propertyManagerId
      );

      return setState((prevState: any) => ({
        ...prevState,
        suiteSignatureList: [...currentSignatures, newSignature],
      }));
    }

    // Default case: Handle Home Owner.
    const primaryContactId: any = suiteFormData.primaryContact?.id || null;
    const newSignature: any = createSignatureObject(
      'Home Owner',
      primaryContactId
    );

    setState((prevState: any) => ({
      ...prevState,
      suiteSignatureList: [...currentSignatures, newSignature],
    }));
  };

  const handleHomeOwnerSignature = (
    image: any,
    signType: any,
    userId: any,
    isEmailBased: any
  ) => {
    const { homeOwnersSigns } = state;

    if (!homeOwnersSigns || homeOwnersSigns.length === 0) {
      return;
    }

    const updatedHomeOwnerSigns = homeOwnersSigns.map((sign: any) => {
      const isTargetSign: boolean = isEmailBased
        ? sign.email === userId
        : sign.userId === userId;

      return isTargetSign ? { ...sign, signature: image, signType } : sign;
    });

    setState((prevState: any) => ({
      ...prevState,
      homeOwnersSigns: updatedHomeOwnerSigns,
    }));
  };

  const removeSignature = (
    role: any,
    userId?: any,
    isEmailBasedRemoval?: any
  ) => {
    const { suiteSignatureList, homeOwnersSigns } = state;

    if (!userId) {
      const filteredSignatures = suiteSignatureList.filter(
        (signature: any) => signature.role !== role
      );

      return setState((prevState: any) => ({
        ...prevState,
        suiteSignatureList: filteredSignatures,
      }));
    }

    if (isEmailBasedRemoval) {
      const filteredHomeOwnerSigns = homeOwnersSigns.filter(
        (sign: any) => sign.email !== userId
      );
      const filteredSuiteSignatures = suiteSignatureList.filter(
        (signature: any) => signature.email !== userId
      );

      return setState((prevState: any) => ({
        ...prevState,
        homeOwnersSigns: filteredHomeOwnerSigns,
        suiteSignatureList: filteredSuiteSignatures,
      }));
    }

    const filteredHomeOwnerSigns = homeOwnersSigns.filter(
      (sign: any) => sign.userId !== userId
    );
    const filteredSuiteSignatures = suiteSignatureList.filter(
      (signature: any) => signature.userId !== userId
    );

    setState((prevState: any) => ({
      ...prevState,
      homeOwnersSigns: filteredHomeOwnerSigns,
      suiteSignatureList: filteredSuiteSignatures,
    }));
  };

  const handleWarrantyStartDateChange = (date: string) => {
    setState((prevState: any) => ({
      ...prevState,
      warrantyStartDate: date,
    }));
  };

  const handleSubmit = async () => {
    const { suiteSignatureList, homeOwnersSigns, notes, warrantyStartDate } =
      state;
    let signatures: any[] = homeOwnersSigns.filter((hs: any) => hs.signature);

    const formattedWarrantyDate: string = warrantyStartDate
      ? moment(warrantyStartDate).format('YYYY-MM-DD')
      : '';

    let params: any = {
      SuiteId: suiteFormData.id,
      LoginUserId: user.id,
      SignatureList: [...suiteSignatureList, ...signatures],
      notes: notes,
      warrantyStartDate: formattedWarrantyDate,
    };

    const isSuccess: boolean = await submitKeyHandoff(params);

    if (isSuccess) {
      closeKeyPickupDialogueAndRefreshContent();
    }
  };

  const addSignee = async () => {
    await fetchSuiteUsersData();
    setState((prevState: any) => ({
      ...prevState,
      isAddFormOpen: true,
    }));
  };

  const onSubmitPopup = (
    type: any,
    selectedHomeOwnerId: any,
    userType: any
  ) => {
    const { homeOwnersSigns } = state;

    if (type === 'cancel') {
      return setState((prevState: any) => ({
        ...prevState,
        isAddFormOpen: false,
      }));
    }

    if (userType === 'New') {
      return setState((prevState: any) => ({
        ...prevState,
        homeOwnersSigns: [...homeOwnersSigns, selectedHomeOwnerId],
        isAddFormOpen: false,
      }));
    }

    const userData: SuiteUser = suiteUsers.find(
      (user: any) => user.id === selectedHomeOwnerId
    );

    if (!userData) {
      return console.error('User not found with id:', selectedHomeOwnerId);
    }

    const newHomeOwnerSign = {
      userId: selectedHomeOwnerId,
      role: 'Home Owner',
      signature: '',
      firstname: userData.firstname,
      lastname: userData.lastname,
      signType: '',
    };

    setState((prevState: any) => ({
      ...prevState,
      homeOwnersSigns: [...homeOwnersSigns, newHomeOwnerSign],
      isAddFormOpen: false,
    }));
  };

  const processSignatureData = (
    suiteSignatureList: any[],
    homeOwnersSigns: any[],
    suiteFormData: any
  ): any => {
    const allPrimarySignatures = suiteSignatureList || [];
    const completedHomeOwnerSignatures: any[] =
      homeOwnersSigns?.filter((signature: any) => signature.signature) || [];

    const propertyDeveloperSignature: any =
      allPrimarySignatures.find(
        (signature: any) => signature.role === 'Property Developer'
      ) || {};

    const primaryHomeOwnerSignature: any =
      allPrimarySignatures.find(
        (signature: any) => signature.role === 'Home Owner'
      ) || {};

    let primaryHomeOwnerName = '';
    let homeOwnerProfileImage = '';

    if (suiteFormData.primaryContact?.id) {
      primaryHomeOwnerName = `${suiteFormData.primaryContact.firstname} ${suiteFormData.primaryContact.lastname}`;
      homeOwnerProfileImage =
        suiteFormData.primaryContact.profileImage || '/assets/avatar.svg';
    } else if (suiteFormData.homeOwners?.id) {
      primaryHomeOwnerName = `${suiteFormData.homeOwners.firstname} ${suiteFormData.homeOwners.lastname}`;
      homeOwnerProfileImage =
        suiteFormData.homeOwners.profileImage || '/assets/avatar.svg';
    }

    const propertyDeveloperName: string = suiteFormData.propertyManager?.id
      ? `${suiteFormData.propertyManager.firstname} ${suiteFormData.propertyManager.lastname}`
      : '';

    return {
      allPrimarySignatures,
      completedHomeOwnerSignatures,
      propertyDeveloperSignature,
      primaryHomeOwnerSignature,
      primaryHomeOwnerName,
      homeOwnerProfileImage,
      propertyDeveloperName,
    };
  };

  const {
    suiteSignatureList,
    homeOwnersSigns,
    notes,
    isAddFormOpen,
    warrantyStartDate,
  } = state;

  const {
    allPrimarySignatures,
    completedHomeOwnerSignatures,
    propertyDeveloperSignature,
    primaryHomeOwnerSignature,
    primaryHomeOwnerName,
    homeOwnerProfileImage,
    propertyDeveloperName,
  } = useMemo(
    () =>
      processSignatureData(suiteSignatureList, homeOwnersSigns, suiteFormData),
    [suiteSignatureList, homeOwnersSigns, suiteFormData]
  );

  useEffect(() => {
    if (validation.status === true) {
      if (validation.message === 'KeyHandoffComponent') {
        onClose();
        dispatch(clearValidation());
      }
    }
  }, [validation, onClose, dispatch]);

  useEffect(() => {
    if (keyPickupError) {
      toast.error(`🚫 ${keyPickupError}`, {
        autoClose: 10000,
        position: 'top-center',
      });
    }
  }, [keyPickupError]);

  return (
    <Form
      headline={'Key Pickup'}
      onClose={onClose}
      onExit={onClose}
      component={
        prLoader ? (
          <Loader center={true} />
        ) : suiteFormData && suiteFormData.id ? (
          <Row>
            {/* Updated to include isFetchingSuiteUsers in loading condition */}
            {loader || keyPickupLoading || isFetchingSuiteUsers ? (
              <Loader center={true} position={true} />
            ) : null}
            <Col xs="12" md="12">
              <div
                className="keyhand_process_details_row"
                style={{ padding: 15 }}
              >
                <Row mdCenter>
                  <Col xs="12" md="4">
                    <div className="col_inr">
                      <div className="keyhand_process_details_box">
                        <h4>Home Owner</h4>
                        <div className="keyhand_process_details_box_img d-flex align-items-center logo_style">
                          <img
                            className="avatar__image"
                            src={homeOwnerProfileImage}
                            alt=""
                          />
                          <span>{primaryHomeOwnerName}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs="12" md="4">
                    <div className="col_inr">
                      <div className="keyhand_process_details_box">
                        <h4>{variables.Suite} Address</h4>
                        <div className="keyhand_process_details_box_img d-flex align-items-center">
                          <span>{suiteFormData.warrantyAddress}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs="12" md="4">
                    <div className="col_inr">
                      <div className="keyhand_process_details_box">
                        <h4>{variables.Suite} Number</h4>
                        <div className="keyhand_process_details_box_img d-flex align-items-center">
                          <span>{suiteFormData.number}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="keyhand_process_details_row instructions">
                <Row>
                  <Col xs="12" md="6">
                    {suiteFormData.keyHandOffInstructions ? (
                      <div>
                        {
                          <div
                            dangerouslySetInnerHTML={{
                              __html: suiteFormData.keyHandOffInstructions,
                            }}
                          />
                        }
                      </div>
                    ) : (
                      ''
                    )}
                  </Col>
                  <Col xs="12" md="6">
                    <div
                      className="ul_list_box1"
                      style={{ paddingLeft: '0px', marginLeft: '0px' }}
                    >
                      <ul>
                        {Array.isArray(suiteFormData?.parkingStalls) &&
                          suiteFormData.parkingStalls.length > 0 && (
                            <SuiteExtraInfo
                              title={'Parking Stalls'}
                              items={suiteFormData.parkingStalls}
                              message={'No parking slots.'}
                            />
                          )}
                        {Array.isArray(suiteFormData?.storageLockers) &&
                          suiteFormData.storageLockers.length > 0 && (
                            <SuiteExtraInfo
                              title={'Storage Lockers'}
                              items={suiteFormData.storageLockers}
                              message={'No storage lockers.'}
                            />
                          )}
                        <SuiteExtraInfo
                          title={'Key Fobs'}
                          items={suiteFormData.keyFobs}
                          message={'No key fobs.'}
                        />
                        <li>
                          Number of keys :{' '}
                          <span>{suiteFormData.numberOfKeys}</span>
                        </li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </div>
              {/* Suite Keys AndValue section - only show if they exist. */}
              {suiteFormData.suiteKeysAndValues &&
                suiteFormData.suiteKeysAndValues.length > 0 && (
                  <>
                    <Row>
                      {suiteFormData.suiteKeysAndValues.map(
                        (item: any, i: any) => {
                          return (
                            <Col xs="12" md="6" mdCenter key={i}>
                              <Row style={{ marginBottom: 20 }}>
                                <Col md="12" mdCenter>
                                  <span
                                    className=""
                                    style={{
                                      fontWeight: 'bold',
                                      marginRight: '8px',
                                    }}
                                    title={item.key}
                                  >
                                    {item.key}:
                                  </span>{' '}
                                  <span className="" title={item.value}>
                                    {item.value}
                                  </span>
                                </Col>
                              </Row>
                            </Col>
                          );
                        }
                      )}
                    </Row>
                  </>
                )}
              {/* Warranty Details with editable warranty start date */}
              <KeyPickupWarrantyDetails
                suiteId={suiteFormData.id}
                warrantyStartDate={warrantyStartDate}
                onWarrantyStartDateChange={handleWarrantyStartDateChange}
              />
              {/* Service Requests - only show if they exist. */}
              {serviceRequests && serviceRequests.length > 0 && (
                <KeyPickupServiceRequestList suiteId={suiteFormData.id} />
              )}
              {/* Important Documents - only show if they exist. */}
              {importantDocuments && importantDocuments.length > 0 && (
                <KeyPickupDocuments
                  importantDocuments={importantDocuments}
                  user={user}
                />
              )}
              <Textarea
                value={notes}
                onChange={(e: any) => {
                  const notesVal: any = e?.target?.value;
                  setState((prevState: any) => ({
                    ...prevState,
                    notes: notesVal,
                  }));
                }}
                label="NOTES"
                placeholder="Notes"
                block
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'right',
                  marginTop: 20,
                }}
              >
                <Button
                  color="complete_purple"
                  label="Add Signee"
                  onClick={() => addSignee()}
                  size="bar"
                  block={undefined}
                  disabled={undefined}
                  outlined={undefined}
                  icon={undefined}
                  outline={undefined}
                  weight={undefined}
                  children={undefined}
                  style={undefined}
                />
              </div>
              <div className="row keypickup">
                <Col xs="12" md="6">
                  <Row mdCenter>
                    <Col md="8">
                      <h4>
                        {propertyDeveloperName} Property Developer Signature
                      </h4>
                    </Col>
                  </Row>
                  <SignatureCanvas
                    type={'keyhandoff'}
                    signeeRole={ROLES.PROPERTY_DEVELOPER}
                    onInsert={(url: any, signType: any) => {
                      handleSignature(url, signType, 'Property Developer');
                    }}
                    signed={
                      propertyDeveloperSignature &&
                      propertyDeveloperSignature.signature
                        ? propertyDeveloperSignature.signature
                        : ''
                    }
                    signType={
                      propertyDeveloperSignature &&
                      propertyDeveloperSignature.signType
                        ? propertyDeveloperSignature.signType
                        : ''
                    }
                    remove={() => removeSignature('Property Developer')}
                    firstName={
                      suiteFormData.propertyManager &&
                      suiteFormData.propertyManager.firstname
                        ? suiteFormData.propertyManager.firstname
                        : ''
                    }
                    lastName={
                      suiteFormData.propertyManager &&
                      suiteFormData.propertyManager.lastname
                        ? suiteFormData.propertyManager.lastname
                        : ''
                    }
                    typeOfSign={'keyhandoff'}
                  />
                </Col>
                {suiteFormData.primaryContact &&
                  suiteFormData.primaryContact.id && (
                    <Col xs="12" md="6">
                      <Row mdCenter>
                        <Col md="8">
                          <h4>{primaryHomeOwnerName} Home Owner Signature</h4>
                        </Col>
                      </Row>
                      <SignatureCanvas
                        type={'keyhandoff'}
                        onInsert={(url: any, signType: any) => {
                          handleSignature(url, signType, 'Home Owner');
                        }}
                        signed={
                          primaryHomeOwnerSignature &&
                          primaryHomeOwnerSignature.signature
                            ? primaryHomeOwnerSignature.signature
                            : ''
                        }
                        signType={
                          primaryHomeOwnerSignature &&
                          primaryHomeOwnerSignature.signType
                            ? primaryHomeOwnerSignature.signType
                            : ''
                        }
                        remove={() => removeSignature('Home Owner')}
                        firstName={
                          suiteFormData.primaryContact &&
                          suiteFormData.primaryContact.firstname
                            ? suiteFormData.primaryContact.firstname
                            : ''
                        }
                        lastName={
                          suiteFormData.primaryContact &&
                          suiteFormData.primaryContact.lastname
                            ? suiteFormData.primaryContact.lastname
                            : ''
                        }
                        typeOfSign={'keyhandoff'}
                      />
                    </Col>
                  )}
              </div>
              <div className="row">
                {homeOwnersSigns && homeOwnersSigns.length > 0
                  ? homeOwnersSigns.map((hs: any, index: number) => {
                      return (
                        <Col xs="12" md="6" key={index}>
                          <Row mdCenter>
                            <Col md="8">
                              <h4>
                                {hs.firstname + ' ' + hs.lastname} Home Owner
                                Signature
                              </h4>
                            </Col>
                          </Row>
                          <SignatureCanvas
                            type={'keyhandoff'}
                            onInsert={(url: any, signType: any) => {
                              handleHomeOwnerSignature(
                                url,
                                signType,
                                hs.userId ? hs.userId : hs.email,
                                !!hs.email
                              );
                            }}
                            signed={hs.signature ? hs.signature : ''}
                            signType={hs.signType ? hs.signType : ''}
                            remove={() =>
                              removeSignature(
                                'Home Owner',
                                hs.userId ? hs.userId : hs.email,
                                !!hs.email
                              )
                            }
                            firstName={hs.firstname}
                            lastName={hs.lastname}
                            typeOfSign={'keyhandoff'}
                          />
                        </Col>
                      );
                    })
                  : null}
              </div>
              {isAddFormOpen ? (
                <AddSigneePopup onSubmitPopup={onSubmitPopup} />
              ) : null}
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 20,
                  position: 'fixed',
                  bottom: '0px',
                  left: '0px',
                  width: '100%',
                  background: 'rgb(255, 255, 255)',
                  padding: '15px',
                  boxShadow: 'rgba(0, 0, 0, 0.3) 0px 0px 0.3rem',
                }}
              >
                <Button
                  color="complete_purple"
                  label="Submit"
                  onClick={() => handleSubmit()}
                  size="bar"
                  disabled={
                    !(
                      (allPrimarySignatures &&
                        allPrimarySignatures.length === 1 &&
                        completedHomeOwnerSignatures.length >= 1) ||
                      (allPrimarySignatures &&
                        allPrimarySignatures.length === 2)
                    )
                  }
                  block={undefined}
                  outlined={undefined}
                  icon={undefined}
                  outline={undefined}
                  weight={undefined}
                  children={undefined}
                  style={undefined}
                />
              </div>
            </Col>
          </Row>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {variables.Suite} details fetching failed.
          </div>
        )
      }
      actions={undefined}
      footer={undefined}
      onSubmit={undefined}
      strapline={undefined}
    />
  );
};

export default KeyHandoffComponent;
